/**
 * Route history tracker — small ring buffer of recently-visited routes.
 *
 * The bug-report UI surfaces the last 5 distinct routes the user walked
 * through before clicking "+", which is far more diagnostic than just
 * "the route I was on when I clicked the button". A real user trace
 * (e.g. /users → /users/abc → /agents → /agents/build → broken page)
 * tells the triager what flow led to the bug without any extra typing.
 *
 * Privacy: routes only — no query strings, no hashes. Same posture as
 * `failed-api-tracker.ts`. Lives entirely in memory; not persisted.
 *
 * Wired from `__root.tsx` via a router-effect that calls `recordRoute`
 * on every location change. The hook deduplicates consecutive same-route
 * inserts so re-renders / fast refresh don't pollute the ring.
 */

const RING_SIZE = 10;
const ring: string[] = [];

/**
 * Append a route to the ring buffer. Consecutive duplicates are
 * collapsed (a re-render of `/users/abc` shouldn't appear five times).
 */
export function recordRoute(pathname: string): void {
  if (typeof pathname !== 'string' || pathname.length === 0) return;
  // Strip any accidental query string / hash if a caller passes the
  // raw URL instead of the pathname.
  const cleaned = pathname.split('?')[0].split('#')[0];
  if (ring.length > 0 && ring[ring.length - 1] === cleaned) {
    return;
  }
  ring.push(cleaned);
  while (ring.length > RING_SIZE) ring.shift();
}

/**
 * Returns the last 5 routes in chronological order (oldest → newest).
 * The bug-report metadata helper passes this straight through to the
 * server, where it's rendered as `A → B → C → …`.
 */
export function getRecentRoutes(): string[] {
  return ring.slice(-5);
}

/** Test-only. */
export function _resetRouteHistoryForTests(): void {
  ring.length = 0;
}
